import React from 'react';
import { Grid, CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import api from '../../services/api';

import { Rewards } from './components/Rewards';
import { UserStatus } from './components/UserStatus';
import { UserReports } from './components/UserReports';
import { NinaMessage } from './components/NinaMessage';
import { IssueDescription } from './components/IssueDescription';
import { useUniversities } from '../../hooks/universities';
import { IssueButtons } from './components/IssueButtons';
import { SearchUser } from './components/SearchUser';
import { messageTemplates } from './utils/messageTemplates';

export function IssueDetails() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { issues } = useUniversities();

  const { userEmail, issueId } = useParams();
  const [ninaMessage, setNinaMessage] = React.useState('');
  const [isEmailInvalid, setIsEmailInvalid] = React.useState(false);
  const [isDefaultMessage, setIsDefaultMessage] = React.useState(true);

  const { isLoading: issueLoading, data: issue } = useQuery(
    ['issue', issueId],
    async () => {
      const issueIdNumber = Number(issueId);
      const { data } = await api.get(`/admin/issues/${issueIdNumber}`);

      return data;
    },
    {
      enabled: !!issueId,
      onError: () => {
        toast.error('Erro ao carregar ticket');
      },
    }
  );

  const { isLoading: userLoading, data: user } = useQuery(
    ['issue-user', userEmail || issue?.email],
    async () => {
      setIsEmailInvalid(false);
      const { data } = issue
        ? await api.get(`/admin/users/${issue?.email}`)
        : await api.get(`/admin/users/${userEmail}`);
      return data;
    },
    {
      enabled: !!issue || !!userEmail,
      onError: () => {
        toast.error('Erro ao carregar usuário');
        setIsEmailInvalid(true);
      },
    }
  );

  const isLoading = issueId ? issueLoading || userLoading : userLoading;

  const updateIssueMutation = useMutation(
    async issueData => {
      const { data } = await api.put(`/admin/issues/${issue.id}`, {
        message: isDefaultMessage
          ? messageTemplates({
              name: user.user.first_name,
              platform: user.user.platform,
              issueId: issue.id,
              countryId: user.user.country_id,
            })[issue.type].find(template =>
              issueData.status === 'resolved' ? template.id === 'defaultApproved' : template.id === 'defaultRejected'
            )?.message
          : ninaMessage,

        status: issueData.status,
      });
      return data;
    },
    {
      onSuccess: () => {
        setNinaMessage('');
        navigateToNextIssue();
        queryClient.invalidateQueries(['issue-user', issue?.email]);
        window.scrollTo(0, 0);
      },
      onError: error => {
        toast(`Ocorreu um erro: ${error.message}`, { type: 'error' });
      },
      onMutate: async issueData => {
        await queryClient.cancelQueries({ queryKey: ['issues'] });

        const previousIssues = queryClient.getQueryData(['issues']);

        queryClient.setQueryData(['issues'], old => ({
          ...old,
          open: {
            ...old.open,
            [issueData.type]: old.open[issueData.type].filter(i => i.id !== issueData.id),
          },
        }));

        return { previousIssues };
      },
      mutationKey: 'archiveIssue',
    }
  );

  const handleUpdateIssue = async status => {
    await updateIssueMutation.mutateAsync({
      ...issue,
      status,
    });
  };

  const infoToChange = {
    first_name: !!issue?.info?.first_name,
    gender: !!issue?.info?.gender,
    major_id: !!issue?.info?.major_id,
    birthday: !!issue?.info?.birthday,
    university_id: !!issue?.info?.university_id,
    handle: !!issue?.info?.handle,
  };

  const navigateToNextIssue = () => {
    const issuesOfType = issues.open[issue.type];

    const currentIndex = issuesOfType.findIndex(i => i.id === issue.id);

    if (currentIndex < issuesOfType.length - 1) {
      navigate(`/issue-details/issue/${issuesOfType[currentIndex + 1].id}`);
    } else if (issuesOfType.length > 1) {
      navigate(`/issue-details/issue/${issuesOfType[0].id}`);
    } else {
      navigate('/issue-list');
    }

    window.scrollTo(0, 0);
  };

  const navigateToPreviousIssue = () => {
    const issuesOfType = issues.open[issue.type];

    const currentIndex = issuesOfType.findIndex(i => i.id === issue.id);

    if (currentIndex > 0) {
      navigate(`/issue-details/issue/${issuesOfType[currentIndex - 1].id}`);
    } else {
      navigate(`/issue-details/issue/${issuesOfType[issuesOfType.length - 1].id}`);
    }

    window.scrollTo(0, 0);
  };

  return (
    <Grid>
      {(!issueId && !userEmail) || isEmailInvalid ? (
        <SearchUser />
      ) : isLoading ? (
        <Grid container justifyContent='center' alignItems='center' style={{ height: '100vh' }}>
          <CircularProgress />
        </Grid>
      ) : (
        user && (
          <>
            {issue && (
              <>
                <IssueButtons
                  onPrevious={navigateToPreviousIssue}
                  onNext={navigateToNextIssue}
                  onApprove={() => handleUpdateIssue('resolved')}
                  onDelete={() => handleUpdateIssue('rejected')}
                  onArchive={() => handleUpdateIssue('archived')}
                />
                <IssueDescription issueData={issue} />
              </>
            )}
            <NinaMessage
              userData={user}
              setMessage={setNinaMessage}
              message={ninaMessage}
              issueType={issue?.type}
              issueId={issue?.id}
              setIsDefaultMessage={setIsDefaultMessage}
            />
            <UserStatus userData={user} infoToChange={issue?.type === 'info change' ? infoToChange : null} />
            <Rewards userData={user} />
            <UserReports userData={user} />
          </>
        )
      )}
      {issue && (
        <IssueButtons
          onPrevious={navigateToPreviousIssue}
          onNext={navigateToNextIssue}
          onApprove={() => handleUpdateIssue('resolved')}
          onDelete={() => handleUpdateIssue('rejected')}
          onArchive={() => handleUpdateIssue('archived')}
        />
      )}
    </Grid>
  );
}
